import {
  DeleteStories,
  GetStories,
  GetStoryLists,
  GetStoryViews,
  PostStory,
  reqDeleteStories,
  reqGetStories,
  reqGetStoryLists,
  reqGetStoryViews,
  reqPostStory,
  ResGetStoryViewsPayload,
  sendViewStories,
  StoryPeerType,
} from '@weblite/ts-action/story'
import * as R from 'ramda'
import { v4 as uuidV4 } from 'uuid'
import { uploadFile } from '@weblite/file'
import { DateString } from '@weblite/ts-action'
import { request, send } from '$setup/ws'
import {
  StoryContent,
  StoryRibbon,
  StoryRibbonPriority,
  UploadingStory,
  UploadingVideo,
} from '$logic/story/types'
import { storiesStorage } from '$logic/story/storage'
import { blobToFile, messageTypeByFileType } from '$func/file'
import { getImageMessageDataFromFile } from '$logic/message'
import { getVideoScreenShot } from '$func/video-screen-shot'
import {
  getImageThumbnail,
  intelligentCompressImage,
} from '$func/image-compression'
import getBlobSrc from '$func/getBlobSrc'
import { sortByDate } from '$helper/functions/sort'
import { STORY_VIDEO_COMPRESSION_HEIGHT } from '$comp/_app/components/story/constants'
import { getVideoDimension } from '$func/video-dimension'
import { showViewStoryModal } from '$comp/modals/view-story-modal/view-story.api'
import { prioritizeStoryRibbons, sortStoryRibbonsByPriority } from './helpers'
import { StoryAnalyticsApi } from './analytics'
import { IS_STORY_ENABLED } from '$func/environmentInfo'
import {
  createUploadReporter,
  UploadSource,
} from '$logic/analytics/reporters/upload'

const sortRibbonsByPriorityAndDate = (storyRibbons: StoryRibbon[]) =>
  sortStoryRibbonsByPriority(
    sortByDate(
      prioritizeStoryRibbons(storyRibbons),
      storyRibbon => storyRibbon.lastStoryAt,
      'descending',
    ),
  )

export const syncStoryRibbons = async () => {
  if (!IS_STORY_ENABLED) return
  const data = await request<GetStoryLists>(reqGetStoryLists({ peers: [] }))
  if (data.error) return

  storiesStorage.setServerStoryRibbonsData(
    sortRibbonsByPriorityAndDate(data.payload.storyLists as StoryRibbon[]),
    true,
  )
}

export const sortStoryRibbons = async () => {
  const storyRibbons = storiesStorage.getServerStoryRibbonsData()

  storiesStorage.setServerStoryRibbonsData(
    sortRibbonsByPriorityAndDate(storyRibbons),
    true,
  )
}

export const getStories = async (storyRibbons: StoryRibbon[]) => {
  const res = await request<GetStories>(
    reqGetStories({
      storyLists: storyRibbons.map(storyRibbon => ({
        peerId: storyRibbon.peerId,
        peerType: storyRibbon.peerType,
        ids: storyRibbon.stories.map(story => story.id),
      })),
    }),
  )
  if (res.error || !res.payload) {
    storyRibbons.forEach(({ peerId, peerType }) => {
      storiesStorage.setStoryData(peerId, peerType, [])
    })
    return
  }

  const responseStories = res.payload.stories

  R.forEach(
    ([peerType, stories1]) => {
      R.forEach(
        ([peerId, stories]) => {
          storiesStorage.setStoryData(
            peerId,
            peerType,
            sortByDate(stories, story => story.updatedAt),
          )
        },
        R.toPairs(R.groupBy(story => story.peerId, stories1)),
      )
    },
    R.toPairs(R.groupBy(story => story.peerType, responseStories)),
  )
}

export const syncStoryViewCount = async (
  peerId: string,
  peerType: StoryPeerType,
  storyId?: string,
) => {
  if (!storyId) return
  const res = await request<GetStories>(
    reqGetStories({
      storyLists: [
        {
          peerId,
          peerType,
          ids: [storyId],
        },
      ],
    }),
  )
  if (res.error || !res.payload || !res.payload.stories[0]?.viewCount) return

  const responseStory = res.payload.stories[0]

  storiesStorage.updateRibbonStoryViewCount({
    peerId,
    peerType,
    storyId,
    viewCount: responseStory.viewCount!,
  })
}

async function uploadImageStory(
  rawFile: File,
  peerId: string,
  peerType: StoryPeerType,
  onUploadStart: () => unknown,
) {
  const file = await intelligentCompressImage(rawFile)
  const uploadId = uuidV4()
  storiesStorage.createUploadingData({
    uploadId,
    file,
    type: 'IMAGE',
    uploadPercent: 0,
    uploadStatus: 'UPLOADING',
    thumbnail: '',
    peerId,
    peerType,
    fileUrl: URL.createObjectURL(file),
    uploadStartedAt: new Date().toString(),
  })
  onUploadStart()
  const { imageThumbnail } = await getImageMessageDataFromFile(file)
  storiesStorage.updateUploadingData({
    uploadId,
    thumbnail: imageThumbnail,
  })

  const uploadReporter = createUploadReporter(UploadSource.STORY)
  uploadReporter.startUpload(file.type, file.size)

  uploadFile({
    uploadId,
    file,
    type: 'infer',
    onProgress: uploadPercent => {
      storiesStorage.updateUploadingData({
        uploadId,
        uploadPercent,
        uploadStatus: 'UPLOADING',
      })
    },
    onComplete: info => {
      storiesStorage.updateUploadingData({
        uploadId,
        uploadStatus: 'SENDING',
        fileId: info.fileId,
      })
      uploadReporter.end({ peerId, peerType, contentType: 'IMAGE' })
      sendStoryToServer({
        peerId,
        peerType,
        uploadId,
        content: [
          { fileId: info.fileId, type: 'IMAGE', thumbnail: imageThumbnail },
        ],
      })
    },

    queue: peerId,
  })
}

const uploadReporters = new Map<
  string,
  ReturnType<typeof createUploadReporter>
>()

async function uploadVideoStory(
  file: File,
  peerId: string,
  peerType: StoryPeerType,
  onUploadStart: () => unknown,
) {
  const uploadId = uuidV4()
  const coverUploadId = `${uploadId}-cover`
  const { duration } = await getVideoDimension(file)
  const screenShot = await getVideoScreenShot(file)
  const thumbnail = await getImageThumbnail(
    blobToFile(screenShot, 'image'),
    100,
  ).then(getBlobSrc)

  const cover = await getImageThumbnail(
    blobToFile(screenShot, 'image.jpg'),
    400,
  )

  storiesStorage.createUploadingData({
    uploadId,
    file,
    type: 'VIDEO',
    uploadPercent: 0,
    uploadStatus: 'UPLOADING',
    thumbnail,
    compressionPercent: 0,
    cover,
    coverUploadId,
    peerId,
    peerType,
    fileUrl: URL.createObjectURL(file),
    duration,
    uploadStartedAt: new Date().toString(),
  })
  onUploadStart()

  const uploadReporter = createUploadReporter(UploadSource.STORY)
  uploadReporter.startUpload(file.type, file.size)
  uploadReporters.set(uploadId, uploadReporter)

  uploadFile({
    uploadId,
    file,
    type: 'infer',
    onProgress: uploadPercent => {
      if (uploadPercent === 100) uploadReporter.startConversion()
      storiesStorage.updateUploadingData({
        uploadId,
        uploadPercent,
        uploadStatus: 'UPLOADING',
      })
    },
    onComplete: info => {
      storiesStorage.updateUploadingData({
        uploadId,
        fileId: info.fileId,
      })
      handleSendVideoStoryToServer({ uploadId, peerId, peerType })
    },
    onConversionProgress: ({ percent }) => {
      storiesStorage.updateUploadingData({
        uploadId,
        compressionPercent: percent,
        uploadStatus: 'COMPRESSING',
      })
      handleSendVideoStoryToServer({ uploadId, peerId, peerType })
    },

    convert: {
      type: 'video',
      options: { format: 'compress', height: STORY_VIDEO_COMPRESSION_HEIGHT },
    },
    queue: peerId,
  })

  const coverUploadReporter = createUploadReporter(UploadSource.STORY)
  coverUploadReporter.startUpload(cover.type, cover.size)
  uploadFile({
    uploadId: coverUploadId,
    file: cover,
    type: 'infer',
    onComplete: info => {
      coverUploadReporter.end({ peerId, peerType, contentType: 'VIDEO' })
      storiesStorage.updateUploadingData({
        uploadId,
        uploadStatus: 'SENDING',
        coverFileId: info.fileId,
      })
      handleSendVideoStoryToServer({ uploadId, peerId, peerType })
    },
    queue: peerId,
  })
}

export async function uploadStory(
  file: File,
  peerId: string,
  peerType: StoryPeerType,
  onUploadStart: () => unknown,
) {
  const storyType = messageTypeByFileType(file.type)
  if (storyType === 'IMAGE') {
    await uploadImageStory(file, peerId, peerType, onUploadStart)
  }
  if (storyType === 'VIDEO') {
    await uploadVideoStory(file, peerId, peerType, onUploadStart)
  }
}

export function handleSendVideoStoryToServer({
  uploadId,
  peerId,
  peerType,
}: {
  peerId: string
  peerType: StoryPeerType
  uploadId: string
}) {
  const { fileId, coverFileId, thumbnail, compressionPercent, duration } =
    (storiesStorage.getUploadingData(uploadId) || {}) as Partial<UploadingVideo>

  const videoUploaded = fileId && compressionPercent === 100

  if (videoUploaded) {
    const uploadReporter = uploadReporters.get(uploadId)
    uploadReporter?.end({ peerId, peerType, contentType: 'VIDEO' })
    uploadReporters.delete(uploadId)
  }

  if (coverFileId && thumbnail && videoUploaded) {
    storiesStorage.updateUploadingData({
      uploadId,
      uploadStatus: 'SENDING',
    })

    sendStoryToServer({
      peerId,
      peerType,
      uploadId,
      content: [
        { fileId, coverId: coverFileId, type: 'VIDEO', thumbnail, duration },
      ],
    })
  }
}

export async function sendStoryToServer({
  peerId,
  peerType,
  content,
  uploadId,
}: {
  peerId: string
  peerType: StoryPeerType
  content: StoryContent
  uploadId: string
}) {
  const res = await request<PostStory>(
    reqPostStory({ peerId, peerType, content }),
  )

  if (res.error) return

  StoryAnalyticsApi.create(peerId, peerType, content[0].type)

  const { id } = res.payload
  const uploadingData = storiesStorage.getUploadingData(
    uploadId,
  ) as UploadingStory

  storiesStorage.addStoryToStories({
    ...res.payload,
    content: [
      {
        fileUrl: uploadingData.fileUrl,
        ...(res.payload.content as StoryContent)[0],
      },
    ],
  })

  storiesStorage.addStoriesToRibbon({
    storyId: id,
    peerId,
    peerType,
    lastStoryAt: uploadingData.uploadStartedAt,
    priority: StoryRibbonPriority.UPLOADING,
  })
  storiesStorage.deleteUploadingData(uploadId)
}

export async function deleteStories(
  peerId: string,
  peerType: StoryPeerType,
  storyId: string,
) {
  const res = await request<DeleteStories>(
    reqDeleteStories({ peerId, peerType, ids: [storyId] }),
  )
  if (res.error) {
    console.error(res.error)
    return
  }

  StoryAnalyticsApi.remove(peerId, peerType, storyId)

  storiesStorage.deleteStoryFromRibbon({ storyId, peerId, peerType })
  storiesStorage.deleteStoryFromStories({ storyId, peerId, peerType })
}

export async function viewStory(
  peerId: string,
  peerType: StoryPeerType,
  storyId: string,
) {
  send(sendViewStories({ peerId, peerType, ids: [storyId] }), true, -1)
  storiesStorage.updateRibbonStoryViewedAt(peerId, peerType, storyId)
}

// export async function updateStoryRibbonViewAll(
//   peerId: string,
//   peerType: StoryPeerType,
// ) {
//   const ribbon = storiesStorage
//     .getServerStoryRibbonsData()
//     .find(rbn => rbn.peerId === peerId && rbn.peerType === peerType)
//   if (!ribbon) return
//   const newViewedAll = getViewedAllStories(ribbon.stories)
//   if (ribbon.viewedAll !== newViewedAll)
//     storiesStorage.updateStoryRibbonViewedAll(peerId, peerType, newViewedAll)
// }

export async function getViewersList(
  peerId: string,
  peerType: StoryPeerType,
  storyId: string,
  before: DateString,
) {
  const res = await request<GetStoryViews>(
    reqGetStoryViews({ peerId, peerType, id: storyId, before }),
  )
  if (res.error) return {} as ResGetStoryViewsPayload
  return res.payload
}

export async function openStory(
  data: Parameters<typeof showViewStoryModal>[0],
) {
  // openStoryPanel()
  await showViewStoryModal({
    ...data,
  })
}
